import { useState, useCallback, useRef, useEffect } from 'react';
import { getFormatControlledValue, searchColumnCustomData } from './columnDefinitions';
import Cartesian from '../shared/Cartesian';
import { buildEmptyRow } from './buildEmptyRow';
export { buildEmptyRow } from './buildEmptyRow';

// A required cell is considered filled if it has a value, or if the column has
// a non-blank default that the server will substitute on save (FieldsMapDefaults
// / effectiveValue). Parent OptionField defaults are not applied, so they still
// require a user-entered value.
export const cellSatisfiesRequired = (column, valueObject) => {
  if (!column.required) return true;
  const hasValue = valueObject && valueObject.value && valueObject.value.length > 0;
  if (hasValue) return true;
  if (column.is_parent) return false;
  return Boolean(column.default_value && column.default_value.length > 0);
};

export const useGridState = (allColumns, updateFieldValue) => {
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [pinnedTopRowData, setPinnedTopRowData] = useState([]);
  const [rowNodes, setRowNodes] = useState([]);
  const [dataValid, setDataValid] = useState(true);
  const [saveClicked, setSaveClicked] = useState(false);

  // Use refs to prevent infinite updates
  const updateInProgress = useRef(false);
  const pendingUpdate = useRef(null);

  const validateInputs = useCallback((api, rowNode) => {
    let valid = true;
    for (const column of allColumns) {
      let valueObject = api.getCellValue({ rowNode: rowNode, colKey: column.field });
      let columnNode = api.getColumn(column.field);
      if (column.required && columnNode && columnNode.isVisible()) {
        if (!cellSatisfiesRequired(column, valueObject)) {
          valid = false;
        }
      }
    }
    return valid;
  }, [allColumns]);

  const updateRowNodes = useCallback((api) => {
    if (!api) return;

    // Clear any pending updates
    if (pendingUpdate.current) {
      clearTimeout(pendingUpdate.current);
    }

    pendingUpdate.current = setTimeout(() => {
      if (updateInProgress.current) return;
      updateInProgress.current = true;

      try {
        let nodes = [];
        let valid = true;
        api.forEachNode((node) => {
          valid = valid && validateInputs(api, node);
          nodes.push(node);
        });
        setDataValid(valid);
        setRowNodes(nodes);
      } finally {
        updateInProgress.current = false;
        pendingUpdate.current = null;
      }
    }, 0);
  }, [validateInputs]);

  // Update row nodes when grid is ready
  useEffect(() => {
    if (gridApi) {
      updateRowNodes(gridApi);
    }
  }, [gridApi, updateRowNodes]);

  const newEmptyRow = useCallback(() => buildEmptyRow(allColumns), [allColumns]);

  const initialPinnedTopRowData = useCallback((initial_pinned_row_data = {}) => {
    let row = {};
    allColumns.forEach((column) => {
      const initialValue = initial_pinned_row_data[column.field];
      const defaultValue = (column.column_type === 'single_select' || column.column_type === 'text') ? [] : column.empty_value;
      row[column.field] = initialValue || defaultValue;
    });
    return [row];
  }, [allColumns]);

  const activeFieldColumns = useCallback(() => {
    let fieldColumns = {};
    if (gridApi) {
      // In AG Grid v32, use gridApi.getColumns() to get all columns
      const allColumns = gridApi.getColumns();
      if (allColumns) {
        for (const column of allColumns) {
          const colDef = column.getColDef();
          if (colDef.field && colDef.field.startsWith('field_') && !colDef.hide) {
            fieldColumns[colDef.field] = colDef;
          }
        }
      }
    }
    return fieldColumns;
  }, [gridApi]);

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    // AG Grid v32: columnApi is deprecated, all column operations are now on params.api
    setGridColumnApi(params.api);
    // Ensure format-controlled fields are calculated on initial load
    setTimeout(() => {
      params.api.refreshCells({ force: true });
    }, 0);
  }, []);

  const addRow = useCallback(() => {
    if (gridApi) {
      const newRow = newEmptyRow();
      gridApi.applyTransaction({ add: [newRow] });
      updateRowNodes(gridApi);
    }
  }, [gridApi, newEmptyRow, updateRowNodes]);

  const deleteRows = useCallback(() => {
    if (gridApi) {
      const selectedRows = gridApi.getSelectedRows();
      gridApi.applyTransaction({ remove: selectedRows });
      updateRowNodes(gridApi);
    }
    return true;
  }, [gridApi, updateRowNodes]);

  const duplicateRows = useCallback(() => {
    if (gridApi) {
      const selectedRows = gridApi.getSelectedRows();
      if (selectedRows.length > 0) {
        const duplicatedRows = selectedRows.map(row => {
          const newRow = { ...row };
          return newRow;
        });
        gridApi.applyTransaction({ add: duplicatedRows });
        updateRowNodes(gridApi);
      }
    }
  }, [gridApi, updateRowNodes]);

  const enforceEmptyStringValue = useCallback((array) => {
    let withoutNullValues = array.filter(i => i);
    return (withoutNullValues.length > 0) ? withoutNullValues : [""];
  }, []);

  const isComboColumn = useCallback((column) => {
    if (!gridApi) return false;
    const columnNode = gridApi.getColumn(column.field);
    return column.allow_combo_generate && columnNode && columnNode.isVisible();
  }, [gridApi]);

  const generateCombinations = useCallback(() => {
    if (!gridApi) return;

    const comboRowData = gridApi.getPinnedTopRow(0).data;
    let cartesianArgs = [];

    for (const column of allColumns) {
      if (isComboColumn(column)) {
        const values = comboRowData[column.field].map(item => item.value);
        cartesianArgs.push(enforceEmptyStringValue([...new Set(values)]));
      }
    }

    const cartesian = new Cartesian(cartesianArgs);
    const results = cartesian.values();
    let newAllRowsData = [];

    for (const row of results) {
      let newRow = {};
      let comboColumnIndex = 0;
      for (const column of allColumns) {
        if (isComboColumn(column)) {
          newRow[column.field] = { value: row[comboColumnIndex] };
          comboColumnIndex++;
        } else {
          newRow[column.field] = JSON.parse(JSON.stringify(comboRowData[column.field]));
        }
      }
      newAllRowsData.push(newRow);
    }

    // AG Grid v32: Use setGridOption instead of setRowData
    gridApi.setGridOption('rowData', newAllRowsData);
    updateRowNodes(gridApi);
  }, [gridApi, allColumns, isComboColumn, enforceEmptyStringValue]);

  const onCellValueChanged = useCallback((params) => {
    if (params.node.isRowPinned()) {
      const customData = searchColumnCustomData(params.context.columnsCustomData, params.column.colId);
      const fieldId = customData && customData.id;
      if (fieldId) {
        const value = params.newValue && params.newValue.value;
        if (updateFieldValue) {
          updateFieldValue(fieldId, value);
        }
      }
    }
    // Force refresh of all rows to update format-controlled fields
    params.api.refreshCells({ force: true });
    updateRowNodes(params.api);
  }, [updateRowNodes, updateFieldValue]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (pendingUpdate.current) {
        clearTimeout(pendingUpdate.current);
      }
    };
  }, []);

  return {
    gridApi,
    gridColumnApi,
    rowData,
    setRowData,
    pinnedTopRowData,
    setPinnedTopRowData,
    rowNodes,
    dataValid,
    saveClicked,
    setSaveClicked,
    onGridReady,
    addRow,
    deleteRows,
    duplicateRows,
    generateCombinations,
    onCellValueChanged,
    activeFieldColumns,
    getFormatControlledValue: (params) => getFormatControlledValue(params, activeFieldColumns()),
    newEmptyRow,
    initialPinnedTopRowData
  };
};